import {lazy} from '@aboutyou/lazy-imports';
import {
  ContentPopperProps,
  useValueAfterHydration,
} from '@aboutyou/ui-components';
import {useCustomerIsVip, useFullSession} from '@aboutyou/user';
import React from 'react';
import {useIsHeaderBasCampaignActive} from '../../features/AboutYouLogo/hooks/useBasCampaign';
import {useGetHomepageTakeover} from '../../features/AboutYouLogo/hooks/useGetHomepageTakeover';
import {useIsTakeoverActive} from '../../features/AboutYouLogo/hooks/useIsTakeoverActive';
import {NotificationCenterButtonShell} from './NotificationCenterButtonShell';

const NotificationCenterRequest = lazy(
  () =>
    import('./NotificationCenterRequest.eager').then(mod => ({
      default: mod.NotificationCenterRequest,
    })),
  {fallback: <NotificationCenterButtonShell />},
);

export const NotificationCenter: React.FC<{
  NotificationCenterStreamComponent: ContentPopperProps['LayoutComponent'];
  headerContent?: React.ReactNode;
  topBodyContent?: React.ReactNode;
  notificationBellRef: React.RefObject<HTMLDivElement | null>;
  NewsletterSubscriptionComponent: ContentPopperProps['LayoutComponent'];
}> = ({
  NotificationCenterStreamComponent,
  headerContent,
  topBodyContent,
  notificationBellRef,
  NewsletterSubscriptionComponent,
}) => {
  const isCampaignHomeTakeoverActive = useIsHeaderBasCampaignActive();
  // TODO: remove the usage of `useValueAfterHydration` https://aboutyou.atlassian.net/browse/ASFE-11723
  const isVipUser = useValueAfterHydration(useCustomerIsVip());
  const fullSession = useValueAfterHydration(useFullSession());

  if (isCampaignHomeTakeoverActive && !isVipUser) {
    return null;
  }

  // On desktop the homepage takeover header logo is not shown for VIP users, instead the VIP logo is rendered,
  // which gives enough space to display the notifications icon
  if (process.env.STACK === 'mobile') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const request = useGetHomepageTakeover();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isActive = useIsTakeoverActive({
      startsAt: request.data?.homepageTakeoverSection?.startsAt,
      endsAt: request.data?.homepageTakeoverSection?.endsAt,
    });

    if (isActive && !isVipUser) {
      return null;
    }
  }

  if (!fullSession) {
    return <NotificationCenterButtonShell />;
  }

  return (
    <NotificationCenterRequest
      NotificationCenterStreamComponent={NotificationCenterStreamComponent}
      headerContent={headerContent}
      topBodyContent={topBodyContent}
      notificationBellRef={notificationBellRef}
      NewsletterSubscriptionComponent={NewsletterSubscriptionComponent}
    />
  );
};
